import React, { useState, useEffect } from 'react';
import { Card, Form, Button, Table, Badge, Alert, Spinner, Modal } from 'react-bootstrap';
import api from '../../services/api';
import { notify } from '../../stores/useNotificationStore';
import { useInterfaceStore } from '../../stores/useInterfaceStore';
import { FiPlus, FiEdit2, FiTrash2, FiPaperclip, FiX, FiSave } from '../../utils/icons';

const NOME_MAX_LENGTH = 80;
const ITEM_DESCRICAO_MAX_LENGTH = 250;

interface CadastroAuxiliarItem {
  id: number;
  tipo: string;
  nome: string;
  empresa_id: number | null;
  nivel: number | null;
  descricao: string | null;
  documento_nome: string | null;
  documento_key: string | null;
  ativo: boolean;
}

export interface CadastroAuxiliarPageProps {
  tipo: string;
  titulo: string;
  descricao?: string;
  fieldLabel: string;
  placeholder: string;
  listTitle: string;
  listEmptyText: string;
  /** Cadastro global (gerenciado só por Consultoria/4Comply) em vez de por empresa ativa. */
  isGlobal?: boolean;
  /** Mostra campo de nível hierárquico (usado só por Tipo da Empresa). */
  showNivel?: boolean;
  /** Mostra upload de documento (usado só por Regulamentação Setorial). */
  showDocumento?: boolean;
  /** Mostra campo de descrição livre por item (usado por Classificação de Conformidade). */
  showItemDescricao?: boolean;
}

const CadastroAuxiliarPage: React.FC<CadastroAuxiliarPageProps> = ({
  tipo, titulo, descricao, fieldLabel, placeholder, listTitle, listEmptyText,
  isGlobal, showNivel, showDocumento, showItemDescricao,
}) => {
  const { empresaId, tipoEmpresa } = useInterfaceStore();
  const canManage = !isGlobal || tipoEmpresa === '4Comply' || tipoEmpresa === 'Consultoria';

  const [itens, setItens] = useState<CadastroAuxiliarItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [busca, setBusca] = useState('');

  // Modal de criação/edição
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [nome, setNome] = useState('');
  const [nivel, setNivel] = useState<string>('');
  const [itemDescricao, setItemDescricao] = useState('');
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    api.getCadastrosAuxiliares(tipo, isGlobal ? undefined : (empresaId || undefined))
      .then(res => { if (res.success && Array.isArray(res.data)) setItens(res.data as CadastroAuxiliarItem[]); })
      .finally(() => setLoading(false));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [tipo, empresaId]);

  const abrirNovo = () => {
    setEditingId(null);
    setNome('');
    setNivel('');
    setItemDescricao('');
    setPendingFile(null);
    setShowModal(true);
  };

  const abrirEdicao = (item: CadastroAuxiliarItem) => {
    setEditingId(item.id);
    setNome(item.nome);
    setNivel(item.nivel !== null && item.nivel !== undefined ? String(item.nivel) : '');
    setItemDescricao(item.descricao || '');
    setPendingFile(null);
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!nome.trim()) { notify.warning(`Informe ${fieldLabel.toLowerCase()}.`); return; }
    if (!isGlobal && !empresaId) { notify.warning('Selecione uma empresa ativa antes de continuar.'); return; }

    setSaving(true);
    try {
      let itemId = editingId;
      if (editingId) {
        const res = await api.updateCadastroAuxiliar(editingId, {
          nome: nome.trim(),
          nivel: showNivel && nivel !== '' ? Number(nivel) : undefined,
          descricao: showItemDescricao ? itemDescricao.trim() : undefined,
        });
        if (!res.success) throw new Error((res.data as any)?.error || 'Erro ao salvar.');
      } else {
        const res = await api.createCadastroAuxiliar({
          tipo,
          nome: nome.trim(),
          empresa_id: isGlobal ? undefined : (empresaId || undefined),
          nivel: showNivel && nivel !== '' ? Number(nivel) : undefined,
          descricao: showItemDescricao ? itemDescricao.trim() : undefined,
        });
        if (!res.success || !res.data) throw new Error((res.data as any)?.error || 'Erro ao salvar.');
        itemId = (res.data as any).id;
      }

      if (showDocumento && pendingFile && itemId) {
        const upRes = await api.uploadCadastroAuxiliarDocumento(itemId, pendingFile);
        if (!upRes.success) throw new Error('Item salvo, mas houve erro ao enviar o documento.');
      }

      notify.success(editingId ? 'Atualizado com sucesso!' : 'Criado com sucesso!');
      setShowModal(false);
      load();
    } catch (err: any) {
      notify.error(err?.message || 'Erro ao salvar.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item: CadastroAuxiliarItem) => {
    if (!window.confirm(`Excluir "${item.nome}"?`)) return;
    const res = await api.deleteCadastroAuxiliar(item.id);
    if (res.success) { notify.success('Excluído com sucesso.'); load(); }
    else notify.error((res.data as any)?.error || 'Erro ao excluir.');
  };

  const handleVerDocumento = async (item: CadastroAuxiliarItem) => {
    const res = await api.getCadastroAuxiliarDocumentoUrl(item.id);
    if (res.success && res.data?.url) window.open(res.data.url, '_blank');
    else notify.error('Erro ao abrir documento.');
  };

  if (!isGlobal && !empresaId) {
    return <Alert variant="danger">Nenhuma empresa ativa. Selecione uma empresa para continuar.</Alert>;
  }

  const itensFiltrados = busca.trim()
    ? itens.filter(i => i.nome.toLowerCase().includes(busca.trim().toLowerCase()))
    : itens;

  const fieldLabelLower = fieldLabel.toLowerCase();

  return (
    <div>
      {/* Cabeçalho + ação principal */}
      <div className="d-flex justify-content-between align-items-start mb-3 gap-2 flex-wrap">
        <div>
          <h3 className="fw-bold mb-1">{titulo}</h3>
          {descricao && <p className="text-muted mb-0">{descricao}</p>}
        </div>
        {canManage && (
          <Button variant="primary" onClick={abrirNovo}><FiPlus /> Novo</Button>
        )}
      </div>

      {!canManage && (
        <Alert variant="warning">
          Este cadastro é gerenciado apenas por empresas do tipo <strong>Consultoria</strong> ou <strong>4Comply</strong>.
          Você tem acesso somente de leitura.
        </Alert>
      )}

      {/* Busca + contador */}
      <div className="d-flex justify-content-between align-items-center mb-2 gap-2 flex-wrap">
        <Form.Control
          type="search"
          value={busca}
          onChange={e => setBusca(e.target.value)}
          placeholder={`Buscar ${fieldLabelLower}`}
          style={{ maxWidth: 320 }}
        />
        <span className="text-muted small">
          {itensFiltrados.length} {listTitle.toLowerCase()}{itensFiltrados.length !== 1 ? 's' : ''} cadastrada{itensFiltrados.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Grid dos registros */}
      <Card>
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-4"><Spinner animation="border" size="sm" /></div>
          ) : itensFiltrados.length === 0 ? (
            <p className="text-muted small p-3 mb-0 fst-italic">
              {itens.length === 0 ? listEmptyText : 'Nenhum item encontrado para essa busca.'}
            </p>
          ) : (
            <Table hover responsive className="mb-0 align-middle">
              <thead className="table-light">
                <tr>
                  <th>Nome</th>
                  {showItemDescricao && <th>Descrição</th>}
                  {showNivel && <th style={{ width: 90 }}>Nível</th>}
                  {showDocumento && <th style={{ width: 200 }}>Documento</th>}
                  <th style={{ width: 110 }}>Status</th>
                  {canManage && <th style={{ width: 100 }} className="text-end">Ações</th>}
                </tr>
              </thead>
              <tbody>
                {itensFiltrados.map(item => (
                  <tr key={item.id}>
                    <td>{item.nome}</td>
                    {showItemDescricao && <td className="text-muted small">{item.descricao || '—'}</td>}
                    {showNivel && <td>{item.nivel ?? '—'}</td>}
                    {showDocumento && (
                      <td>
                        {item.documento_nome ? (
                          <Button variant="link" size="sm" className="p-0 align-baseline text-truncate" style={{ maxWidth: 180 }}
                            onClick={() => handleVerDocumento(item)}>
                            <FiPaperclip /> {item.documento_nome}
                          </Button>
                        ) : <span className="text-muted small">—</span>}
                      </td>
                    )}
                    <td>
                      <Badge bg={item.ativo === false ? 'warning' : 'success'} text={item.ativo === false ? 'dark' : undefined}>
                        {item.ativo === false ? 'inativa' : 'ativa'}
                      </Badge>
                    </td>
                    {canManage && (
                      <td className="text-end">
                        <div className="d-flex gap-1 justify-content-end">
                          <Button size="sm" variant="outline-primary" onClick={() => abrirEdicao(item)}><FiEdit2 /></Button>
                          <Button size="sm" variant="outline-danger" onClick={() => handleDelete(item)}><FiTrash2 /></Button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {/* Modal de criação/edição */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{editingId ? `Editar ${fieldLabelLower}` : `Novo ${fieldLabelLower}`}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={e => { e.preventDefault(); handleSave(); }}>
          <Modal.Body>
            <p className="text-muted small mb-3">O nome deve ser único e não pode ficar em branco.</p>

            <Form.Group className="mb-3">
              <Form.Label>{fieldLabel}</Form.Label>
              <Form.Control
                value={nome}
                onChange={e => setNome(e.target.value.slice(0, NOME_MAX_LENGTH))}
                placeholder={placeholder}
                maxLength={NOME_MAX_LENGTH}
                autoFocus
              />
              <Form.Text className="text-muted">{nome.length}/{NOME_MAX_LENGTH} caracteres</Form.Text>
            </Form.Group>

            {showItemDescricao && (
              <Form.Group className="mb-3">
                <Form.Label>Descrição</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={itemDescricao}
                  onChange={e => setItemDescricao(e.target.value.slice(0, ITEM_DESCRICAO_MAX_LENGTH))}
                  placeholder="Descreva brevemente o que esse item significa"
                />
                <Form.Text className="text-muted">{itemDescricao.length}/{ITEM_DESCRICAO_MAX_LENGTH} caracteres</Form.Text>
              </Form.Group>
            )}

            {showNivel && (
              <Form.Group className="mb-3">
                <Form.Label>
                  Nível na hierarquia{' '}
                  <small className="text-muted">(0 = mais alto; um tipo só pode criar empresas de nível maior que o seu)</small>
                </Form.Label>
                <Form.Control
                  type="number" min={0} style={{ maxWidth: 160 }}
                  value={nivel}
                  onChange={e => setNivel(e.target.value)}
                />
              </Form.Group>
            )}

            {showDocumento && (
              <Form.Group className="mb-2">
                <Form.Label>Documento <small className="text-muted">(opcional)</small></Form.Label>
                <Form.Control
                  type="file"
                  onChange={e => setPendingFile((e.target as HTMLInputElement).files?.[0] || null)}
                />
                {editingId && itens.find(i => i.id === editingId)?.documento_nome && (
                  <div className="text-muted small mt-1">
                    Documento atual: {itens.find(i => i.id === editingId)?.documento_nome}
                  </div>
                )}
              </Form.Group>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)} disabled={saving}><FiX /> Cancelar</Button>
            <Button variant="primary" type="submit" disabled={saving}>
              {saving ? <Spinner as="span" size="sm" animation="border" /> : <><FiSave /> Salvar</>}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default CadastroAuxiliarPage;
